import { useEffect, useRef } from "react";
import { useTranslations, languageOptions, Language } from "@/hooks/useTranslations";
import { useCurrency, currencyOptions, Currency } from "@/hooks/useCurrency";
import { Globe, DollarSign, X } from "lucide-react";

// Map languages to default currencies
const languageToCurrency: Record<Language, Currency> = {
  en: "USD",
  ja: "JPY",
  ko: "KRW",
};

type PanelMode = "language" | "currency" | null;

interface HeaderPreferencesBarProps {
  activePanel: PanelMode;
  onClose: () => void;
}

const HeaderPreferencesBar = ({ activePanel, onClose }: HeaderPreferencesBarProps) => {
  const { language, setLanguage } = useTranslations();
  const { currency, setCurrency } = useCurrency();
  const barRef = useRef<HTMLDivElement>(null);

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
    setCurrency(languageToCurrency[newLanguage]);
    window.location.reload();
  };

  const handleCurrencyChange = (newCurrency: Currency) => {
    setCurrency(newCurrency);
    window.location.reload();
  };

  // Close on Escape
  useEffect(() => {
    if (!activePanel) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [activePanel, onClose]);

  return (
    <div
      ref={barRef}
      className={`hidden md:block w-full overflow-hidden border-t border-border/50 transition-all duration-300 ease-out bg-white ${
        activePanel
          ? "max-h-12 opacity-100"
          : "max-h-0 opacity-0 border-t-transparent"
      }`}
      role="region"
      aria-label="Preferences"
    >
      <div className="container h-12 flex items-center justify-between">
        {/* Content area with crossfade */}
        <div className="relative flex-1 flex items-center">
          {/* Language options */}
          <div
            className={`flex items-center gap-6 transition-all duration-300 ease-out ${
              activePanel === "language"
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-1 pointer-events-none absolute"
            }`}
          >
            <span className="text-xs uppercase tracking-widest text-neutral-500 mr-2">
              <Globe className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
              Language
            </span>
            {languageOptions.map((option) => (
              <button
                key={option.code}
                onClick={() => handleLanguageChange(option.code as Language)}
                className={`text-sm tracking-wide transition-all duration-200 ${
                  language === option.code
                    ? "text-neutral-900 font-medium"
                    : "text-neutral-500 hover:text-neutral-900"
                }`}
              >
                <span>{option.nativeLabel}</span>
                <span className="text-xs text-neutral-400 ml-1">({option.label})</span>
              </button>
            ))}
          </div>

          {/* Currency options */}
          <div
            className={`flex items-center gap-6 transition-all duration-300 ease-out ${
              activePanel === "currency"
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-1 pointer-events-none absolute"
            }`}
          >
            <span className="text-xs uppercase tracking-widest text-neutral-500 mr-2">
              <DollarSign className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
              Currency
            </span>
            {currencyOptions.map((option) => (
              <button
                key={option.code}
                onClick={() => handleCurrencyChange(option.code as Currency)}
                className={`text-sm tracking-wide transition-all duration-200 ${
                  currency === option.code
                    ? "text-neutral-900 font-medium"
                    : "text-neutral-500 hover:text-neutral-900"
                }`}
              >
                <span className="mr-1">{option.symbol}</span>
                <span>{option.code}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="text-neutral-400 hover:text-neutral-900 transition-all duration-200 p-1"
          aria-label="Close preferences"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default HeaderPreferencesBar;
